import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  PiggyBank,
  AlertCircle,
  Lightbulb,
  Calendar,
  ArrowRight,
  Plus,
  FolderCog,
  Sparkles,
  Loader2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../AuthContext';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import CategoryManager from './CategoryManager';
import {
  calculateMonthlySummary,
  calculateCategoryTotals,
  calculateBudgetHealthScore
} from '../../utils/budgeting/budgetCalculations';
import { formatCurrency } from '../../countryConfig';

const BudgetOverview = ({ country, currency, onNavigate }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [budgetItems, setBudgetItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categoryTotals, setCategoryTotals] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [insights, setInsights] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [familyMemberBreakdown, setFamilyMemberBreakdown] = useState([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadBudgetData();
    }
  }, [currentUser, selectedMonth]);

  const loadBudgetData = async () => {
    try {
      setLoading(true);

      // Load budget items for current month
      const budgetItemsRef = collection(db, `users/${currentUser.uid}/budgetItems`);
      const budgetItemsSnapshot = await getDocs(budgetItemsRef);

      const items = [];
      budgetItemsSnapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() });
      });

      setBudgetItems(items);

      // Load transactions for current month
      const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
      const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);

      const transactionsRef = collection(db, `users/${currentUser.uid}/transactions`);
      const transactionsQuery = query(
        transactionsRef,
        where('date', '>=', startOfMonth),
        where('date', '<=', endOfMonth),
        orderBy('date', 'desc')
      );

      const transactionsSnapshot = await getDocs(transactionsQuery);
      const txns = [];
      transactionsSnapshot.forEach(doc => {
        txns.push({ id: doc.id, ...doc.data() });
      });

      setTransactions(txns);

      // Calculate summary
      const summaryData = calculateMonthlySummary(items, txns);
      setSummary(summaryData);

      // Calculate category totals
      const catTotals = calculateCategoryTotals(items);
      setCategoryTotals(catTotals.slice(0, 10)); // Top 10 categories

      // Calculate health score
      const health = calculateBudgetHealthScore(items);
      setHealthScore(health);

      // Calculate family member breakdown
      const memberBreakdown = {};
      items.filter(item => item.owner && item.type === 'expense').forEach(item => {
        const owner = item.owner;
        if (!memberBreakdown[owner]) {
          memberBreakdown[owner] = {
            name: owner,
            total: 0,
            items: []
          };
        }
        const monthly = item.monthlyEquivalent || 0;
        memberBreakdown[owner].total += monthly;
        memberBreakdown[owner].items.push(item);
      });

      const breakdown = Object.values(memberBreakdown).sort((a, b) => b.total - a.total);
      setFamilyMemberBreakdown(breakdown);

      // Generate basic insights
      generateInsights(summaryData, catTotals);

    } catch (error) {
      console.error('Error loading budget data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = (summaryData, catTotals) => {
    const generatedInsights = [];

    // Insight 1: Savings rate
    if (summaryData.savingsRate > 0) {
      if (summaryData.savingsRate >= 20) {
        generatedInsights.push({
          type: 'positive',
          icon: <TrendingUp className="w-5 h-5" />,
          title: 'Excellent Savings Rate',
          message: `You're saving ${summaryData.savingsRate.toFixed(1)}% of your income. This is above the recommended 20% benchmark.`
        });
      } else if (summaryData.savingsRate >= 10) {
        generatedInsights.push({
          type: 'neutral',
          icon: <PiggyBank className="w-5 h-5" />,
          title: 'Good Savings Habit',
          message: `Your savings rate is ${summaryData.savingsRate.toFixed(1)}%. Consider increasing to 20% for optimal financial health.`
        });
      } else {
        generatedInsights.push({
          type: 'warning',
          icon: <AlertCircle className="w-5 h-5" />,
          title: 'Low Savings Rate',
          message: `Your savings rate is ${summaryData.savingsRate.toFixed(1)}%. Aim to save at least 10-20% of your income.`
        });
      }
    }

    // Insight 2: Largest expense category
    if (catTotals.length > 0) {
      const largest = catTotals[0];
      if (largest.percentage > 35) {
        generatedInsights.push({
          type: 'warning',
          icon: <AlertCircle className="w-5 h-5" />,
          title: 'High Category Spending',
          message: `${largest.categoryName} represents ${largest.percentage.toFixed(1)}% of your expenses. Consider reviewing this category for optimization.`
        });
      }
    }

    // Insight 3: Fixed vs Variable costs
    if (summaryData.fixedPercentage > 70) {
      generatedInsights.push({
        type: 'neutral',
        icon: <Lightbulb className="w-5 h-5" />,
        title: 'High Fixed Costs',
        message: `${summaryData.fixedPercentage.toFixed(1)}% of your expenses are fixed. This limits flexibility in your budget.`
      });
    }

    // Insight 4: Cash flow status
    if (summaryData.surplus < 0) {
      generatedInsights.push({
        type: 'warning',
        icon: <TrendingDown className="w-5 h-5" />,
        title: 'Negative Cash Flow',
        message: `You're spending ${formatCurrency(Math.abs(summaryData.surplus), country)} more than you earn each month.`
      });
    } else if (summaryData.surplus > summaryData.totalMonthlyIncome * 0.3) {
      generatedInsights.push({
        type: 'positive',
        icon: <TrendingUp className="w-5 h-5" />,
        title: 'Strong Cash Flow',
        message: `You have ${formatCurrency(summaryData.surplus, country)} surplus each month. Consider investing this for growth.`
      });
    }

    setInsights(generatedInsights.slice(0, 5)); // Show max 5 insights
  };

  const getAIInsights = async () => {
    if (!summary || !categoryTotals || categoryTotals.length === 0) {
      alert('Please add budget items first to get AI insights');
      return;
    }

    try {
      setLoadingAI(true);

      // Prepare data to send to AI (sanitized - no PII)
      const topCategories = categoryTotals.slice(0, 10).map(cat => ({
        name: cat.categoryName,
        amount: Math.round(cat.amount),
        percentage: Math.round(cat.percentage)
      }));

      const requestData = {
        totalIncome: Math.round(summary.totalMonthlyIncome),
        totalExpenses: Math.round(summary.totalMonthlyExpenses),
        savingsRate: Math.round(summary.savingsRate),
        fixedPercentage: Math.round(summary.fixedPercentage || 0),
        variablePercentage: Math.round(summary.variablePercentage || 0),
        topCategories,
        country,
        currency
      };

      // Call Cloud Function
      const generateBudgetInsights = httpsCallable(functions, 'generateBudgetInsights');
      const result = await generateBudgetInsights({ sanitizedBudgetData: requestData });

      if (result.data && result.data.insights) {
        setAiInsights(result.data.insights);
      } else {
        alert('No insights returned from AI. Please try again.');
      }

    } catch (error) {
      console.error('Error getting AI insights:', error);

      if (error.code === 'functions/resource-exhausted') {
        alert('Rate limit exceeded. You can get 5 AI insights per hour. Please try again later.');
      } else if (error.code === 'functions/unauthenticated') {
        alert('Please sign in to use AI insights.');
      } else {
        alert(`Failed to get AI insights: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoadingAI(false);
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 dark:bg-green-900/20';
    if (score >= 60) return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
    return 'text-red-600 bg-red-50 dark:bg-red-900/20';
  };

  const getInsightTypeColor = (type) => {
    if (type === 'positive') return 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200';
    if (type === 'warning') return 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200';
    return 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
  };

  // Chart colors
  const CATEGORY_COLORS = [
    '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#a855f7'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Budget Overview</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track your household income, expenses, and savings
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowCategoryManager(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <FolderCog className="w-4 h-4" />
            Manage Categories
          </button>
          <button
            onClick={getAIInsights}
            disabled={loadingAI || !summary}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingAI ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {loadingAI ? 'Getting Insights...' : 'AI Insights'}
          </button>
          <button
            onClick={() => onNavigate && onNavigate('monthly-budget')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Budget Item
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Monthly Income */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5" />
            <span className="text-xs opacity-80">Monthly</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Total Income</h3>
          <p className="text-2xl font-bold mt-1">
            {summary ? formatCurrency(summary.totalMonthlyIncome, country) : formatCurrency(0, country)}
          </p>
        </div>

        {/* Monthly Expenses */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-red-500 to-orange-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TrendingDown className="w-5 h-5" />
            <span className="text-xs opacity-80">Monthly</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Total Expenses</h3>
          <p className="text-2xl font-bold mt-1">
            {summary ? formatCurrency(summary.totalMonthlyExpenses, country) : formatCurrency(0, country)}
          </p>
        </div>

        {/* Monthly Surplus */}
        <div className={`relative overflow-hidden rounded-xl p-6 text-white shadow-lg ${
          summary && summary.surplus >= 0
            ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
            : 'bg-gradient-to-br from-gray-500 to-gray-600'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5" />
            <span className="text-xs opacity-80">Monthly</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Surplus</h3>
          <p className="text-2xl font-bold mt-1">
            {summary ? formatCurrency(summary.surplus, country) : formatCurrency(0, country)}
          </p>
        </div>

        {/* Savings Rate */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <PiggyBank className="w-5 h-5" />
            <span className="text-xs opacity-80">Rate</span>
          </div>
          <h3 className="text-sm font-medium opacity-90">Savings Rate</h3>
          <p className="text-2xl font-bold mt-1">
            {summary ? `${summary.savingsRate.toFixed(1)}%` : '0%'}
          </p>
        </div>
      </div>

      {/* Health Score Card */}
      {healthScore && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Budget Health Score</h3>
          <div className="flex items-center gap-4">
            <div className={`text-4xl font-bold rounded-full w-20 h-20 flex items-center justify-center ${getHealthScoreColor(healthScore.score)}`}>
              {healthScore.score}
            </div>
            <div className="flex-1">
              <p className="text-lg font-medium text-gray-900 dark:text-white">{healthScore.rating}</p>
              <div className="mt-2 space-y-1">
                {healthScore.factors.map((factor, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{factor.name}</span>
                    <span className={`font-medium ${
                      factor.status === 'good' ? 'text-green-600' :
                      factor.status === 'fair' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {factor.score}/{factor.maxScore}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section - Full Width on Mobile */}
      <div className="space-y-6">
        {/* Category Breakdown - Pie Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">Spending by Category</h3>
          {categoryTotals.length > 0 ? (
            <div className="w-full">
              {/* Mobile View with Legend */}
              <div className="md:hidden">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={categoryTotals}
                      dataKey="amount"
                      nameKey="categoryName"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(entry) => `${entry.percentage.toFixed(0)}%`}
                      labelLine={false}
                    >
                      {categoryTotals.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [formatCurrency(value, country), props.payload.categoryName]}
                      contentStyle={{
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        padding: '8px 12px'
                      }}
                      itemStyle={{ color: 'white' }}
                      labelStyle={{ color: 'white', fontWeight: 'bold', marginBottom: '4px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Custom Legend */}
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  {categoryTotals.map((entry, index) => (
                    <div key={`legend-${index}`} className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded flex-shrink-0"
                        style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                      />
                      <span className="text-gray-700 dark:text-gray-300 truncate">
                        {entry.categoryName}: {entry.percentage.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Desktop View */}
              <div className="hidden md:block">
                <ResponsiveContainer width="100%" height={420}>
                  <PieChart>
                    <Pie
                      data={categoryTotals}
                      dataKey="amount"
                      nameKey="categoryName"
                      cx="50%"
                      cy="50%"
                      outerRadius={150}
                      label={(entry) => `${entry.percentage.toFixed(0)}%`}
                      labelLine={false}
                    >
                      {categoryTotals.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [formatCurrency(value, country), props.payload.categoryName]}
                      contentStyle={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                      itemStyle={{ color: 'white' }}
                      labelStyle={{ color: 'white', fontWeight: 'bold', marginBottom: '4px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Desktop Legend */}
                <div className="mt-4 grid grid-cols-3 lg:grid-cols-5 gap-3 text-sm">
                  {categoryTotals.map((entry, index) => (
                    <div key={`legend-desktop-${index}`} className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded flex-shrink-0"
                        style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                      />
                      <span className="text-gray-700 dark:text-gray-300 truncate" title={entry.categoryName}>
                        {entry.categoryName}: {entry.percentage.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <PiggyBank className="w-12 h-12 mb-2" />
              <p>No expenses to display</p>
              <button
                onClick={() => onNavigate && onNavigate('monthly-budget')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Add your first budget item
              </button>
            </div>
          )}
        </div>

        {/* Top Categories - Bar Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">Expense Categories</h3>
          {categoryTotals.length > 0 ? (
            <div className="w-full">
              {/* Mobile View */}
              <ResponsiveContainer width="100%" height={Math.max(350, categoryTotals.length * 45)} className="md:hidden">
                <BarChart data={categoryTotals} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    tickCount={5}
                    tickFormatter={(value) => `${currency}${value.toLocaleString()}`}
                  />
                  <YAxis
                    dataKey="categoryName"
                    type="category"
                    width={100}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(value, country)}
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      padding: '8px 12px'
                    }}
                  />
                  <Bar dataKey="amount" radius={[0, 8, 8, 0]}>
                    {categoryTotals.map((entry, index) => (
                      <Cell key={`bar-cell-mobile-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {/* Desktop View */}
              <ResponsiveContainer width="100%" height={Math.max(320, categoryTotals.length * 40)} className="hidden md:block">
                <BarChart data={categoryTotals} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickCount={7}
                    tickFormatter={(value) => `${currency}${value.toLocaleString()}`}
                  />
                  <YAxis dataKey="categoryName" type="category" width={150} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value, country)}
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                  />
                  <Bar dataKey="amount" radius={[0, 8, 8, 0]}>
                    {categoryTotals.map((entry, index) => (
                      <Cell key={`bar-cell-desktop-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <p>No data to display</p>
            </div>
          )}
        </div>
      </div>

      {/* Insights Panel */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {aiInsights.length > 0 ? (
              <Sparkles className="w-5 h-5 text-purple-500" />
            ) : (
              <Lightbulb className="w-5 h-5 text-yellow-500" />
            )}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {aiInsights.length > 0 ? 'AI-Powered Insights' : 'Budget Insights'}
            </h3>
          </div>
          {aiInsights.length > 0 && (
            <button
              onClick={() => setAiInsights([])}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Show Basic Insights
            </button>
          )}
        </div>

        {/* AI Insights (priority display) */}
        {aiInsights.length > 0 ? (
          <div className="space-y-3">
            {aiInsights.map((insight, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800"
              >
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                      {insight.title}
                    </h4>
                    <p className="text-sm text-purple-800 dark:text-purple-200 mb-2">
                      {insight.insight}
                    </p>
                    {insight.recommendation && (
                      <div className="mt-2 p-2 bg-white/50 dark:bg-gray-800/50 rounded border-l-2 border-purple-500">
                        <p className="text-xs font-medium text-purple-900 dark:text-purple-100">
                          💡 Recommendation:
                        </p>
                        <p className="text-xs text-purple-800 dark:text-purple-200 mt-1">
                          {insight.recommendation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
              Powered by Gemini AI • Rate limit: 5 insights per hour
            </p>
          </div>
        ) : insights.length > 0 ? (
          /* Rule-based Insights */
          <div className="space-y-3">
            {insights.map((insight, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg ${getInsightTypeColor(insight.type)}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{insight.icon}</div>
                  <div className="flex-1">
                    <h4 className="font-medium mb-1">{insight.title}</h4>
                    <p className="text-sm opacity-90">{insight.message}</p>
                  </div>
                </div>
              </div>
            ))}
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
              Click "AI Insights" above for personalized AI-powered recommendations
            </p>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            Add budget items and transactions to see personalized insights about your spending.
          </p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={() => onNavigate && onNavigate('monthly-budget')}
            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
          >
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Manage Budget</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Edit income & expenses</p>
            </div>
            <ArrowRight className="w-5 h-5 text-blue-600" />
          </button>

          <button
            onClick={() => onNavigate && onNavigate('transactions')}
            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
          >
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Import Transactions</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Upload CSV file</p>
            </div>
            <ArrowRight className="w-5 h-5 text-blue-600" />
          </button>

          <button
            onClick={() => onNavigate && onNavigate('budget-trends')}
            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
          >
            <div>
              <p className="font-medium text-gray-900 dark:text-white">View Trends</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Historical analysis</p>
            </div>
            <ArrowRight className="w-5 h-5 text-blue-600" />
          </button>
        </div>
      </div>

      {/* Family Member Spending Breakdown - Moved to Bottom */}
      {familyMemberBreakdown.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">Spending by Family Member</h3>
          <div className="space-y-3">
            {familyMemberBreakdown.map((member, idx) => {
              const percentage = summary ? (member.total / summary.totalMonthlyExpenses) * 100 : 0;
              return (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                        {member.name}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {percentage.toFixed(1)}% of expenses
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 sm:h-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 sm:h-3 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {member.items.length} item{member.items.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(member.total, country)}/mo
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-4">
            💡 Tip: Assign expenses to family members to track individual spending patterns
          </p>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          onClose={() => setShowCategoryManager(false)}
          onCategoryAdded={() => {
            loadBudgetData();
          }}
        />
      )}
    </div>
  );
};

export default BudgetOverview;
